import { type CSSProperties } from 'react';

import { blue } from '@air/colors';
import { normalizeColorDropdownPickerHex } from '@air/component-color-dropdown-picker/normalizeColorDropdownPickerHex';

/** Matches `blue.blue9` — the custom swatch before the user picks a color. */
export const WORKSPACE_AGENT_GUIDED_VARIANT_DEFAULT_CUSTOM_COLOR = blue.blue9.hex;

/**
 * Local mat only. The source still includes its original background — the checkerboard shows a
 * transparent choice before Generate.
 */
export const WORKSPACE_AGENT_GUIDED_VARIANT_CHECKERBOARD_STYLE: CSSProperties = {
  backgroundColor: '#ffffff',
  backgroundImage:
    'linear-gradient(45deg, #d4d4d4 25%, transparent 25%), linear-gradient(-45deg, #d4d4d4 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #d4d4d4 75%), linear-gradient(-45deg, transparent 75%, #d4d4d4 75%)',
  backgroundPosition: '0 0, 0 6px, 6px -6px, -6px 0',
  backgroundSize: '12px 12px',
};

const CUSTOM_COLOR_WHEEL_STYLE: CSSProperties = {
  backgroundImage: 'conic-gradient(#ef4444, #eab308, #22c55e, #3b82f6, #a855f7, #ef4444)',
};

export type WorkspaceAgentGuidedVariantColorOptionId = 'transparent' | 'white' | 'black' | 'custom';

export type WorkspaceAgentGuidedVariantColorOption = {
  id: WorkspaceAgentGuidedVariantColorOptionId;
  label: string;
  swatchStyle: CSSProperties;
};

export const WORKSPACE_AGENT_GUIDED_VARIANT_COLOR_OPTIONS: readonly WorkspaceAgentGuidedVariantColorOption[] = [
  { id: 'transparent', label: 'Transparent', swatchStyle: WORKSPACE_AGENT_GUIDED_VARIANT_CHECKERBOARD_STYLE },
  { id: 'white', label: 'White', swatchStyle: { backgroundColor: '#FFFFFF' } },
  { id: 'black', label: 'Black', swatchStyle: { backgroundColor: '#000000' } },
  { id: 'custom', label: 'Custom', swatchStyle: CUSTOM_COLOR_WHEEL_STYLE },
];

export const getWorkspaceAgentGuidedVariantColorOptionLabel = (
  optionId: WorkspaceAgentGuidedVariantColorOptionId,
): string => WORKSPACE_AGENT_GUIDED_VARIANT_COLOR_OPTIONS.find((option) => option.id === optionId)?.label ?? optionId;

/** Solid canvas color for the prompt and preview. Transparent stays null so the cutout keeps its alpha. */
export const getWorkspaceAgentGuidedVariantCanvasColor = (
  optionId: WorkspaceAgentGuidedVariantColorOptionId,
  customColor: string,
): string | null => {
  if (optionId === 'transparent') {
    return null;
  }

  if (optionId === 'white') {
    return '#FFFFFF';
  }

  if (optionId === 'black') {
    return '#000000';
  }

  return normalizeColorDropdownPickerHex(customColor) ?? WORKSPACE_AGENT_GUIDED_VARIANT_DEFAULT_CUSTOM_COLOR;
};
